import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Header,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Table,
  Textarea,
} from "@cloudscape-design/components";
import { api } from "@/core/api/client";
import type { Report } from "@/core/api/types";
import { CommentsCell } from "@/core/ui/CommentsCell";
import { lifecycleColor, lifecycleOf } from "@/core/ui/reportStatus";
import { formatET } from "@/core/ui/time";
import { deleteReport, listReports } from "./api";
import { UploadModal } from "./UploadModal";

/** MVP gate: the AI only needs the fiscal year end before an upload (#41). */
function profileIsComplete(profile: Record<string, unknown> | string | null | undefined): boolean {
  const p = typeof profile === "string" ? (safeParse(profile) ?? {}) : (profile ?? {});
  return typeof p.fy_end_month_day === "string" && p.fy_end_month_day.trim().length > 0;
}

function safeParse(s: string): Record<string, unknown> | null {
  try {
    return JSON.parse(s) as Record<string, unknown>;
  } catch {
    return null;
  }
}

interface Props {
  onSelectReport: (reportId: string) => void;
  onNavigateProfile: () => void;
}

interface FYGroup {
  label: string; // "FY2025", "Pending ingest"
  reports: Report[];
}

/** Group reports by the cover-FY the user CHOSE at upload (#50). This is
 *  deterministic — `fy_end` is set when the report is created and never moves,
 *  so a report stays in its FY group from upload (no "Pending ingest → jump to
 *  another group once AI extracts period_end"). Newest FY first. */
const COMPLETED_GROUP = "Completed";

function groupByFY(reports: Report[]): FYGroup[] {
  const buckets = new Map<string, Report[]>();
  for (const r of reports) {
    // #121: completed reports live in their own group (kept visible, retained for
    // 90 days) rather than mixed into their FY — so an active FY group isn't
    // cluttered with finished work.
    const label =
      r.lifecycle_status === "complete"
        ? COMPLETED_GROUP
        : (() => {
            const year = r.fy_end?.slice(0, 4);
            return year && /^\d{4}$/.test(year) ? `FY${year}` : "No FY set";
          })();
    if (!buckets.has(label)) buckets.set(label, []);
    buckets.get(label)!.push(r);
  }
  // "No FY set" first, FY groups newest-first, "Completed" always last.
  const rank = (l: string) => (l === "No FY set" ? -1 : l === COMPLETED_GROUP ? 2 : 0);
  return [...buckets.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || b.localeCompare(a))
    .map(([label, items]) => ({ label, reports: items }));
}

export function ReportsList({ onSelectReport, onNavigateProfile }: Props) {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  // #85: active guardrail — clicking Upload with no fiscal year-end set opens
  // this prompt (routes to Company profile) instead of a passive disabled button.
  const [gateOpen, setGateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Report | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  // null = not yet checked; gates the Upload button until the profile is done (#41).
  const [profileComplete, setProfileComplete] = useState<boolean | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setReports(await listReports());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    api
      .get<{ company: { profile: Record<string, unknown> | string | null } }>("/company")
      .then((r) => setProfileComplete(profileIsComplete(r.company.profile)))
      .catch(() => setProfileComplete(true)); // fail-open: a fetch error shouldn't block uploads
  }, [refresh]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteReport(deleteTarget.id);
      setDeleteTarget(null);
      setDeleteConfirmText("");
      await refresh();
    } finally {
      setDeleting(false);
    }
  };

  const expectedConfirm = deleteTarget?.service_org_name || "";
  const confirmMatch = deleteConfirmText.trim() === expectedConfirm.trim() && expectedConfirm !== "";

  const columns = useMemo(
    () => [
      {
        id: "service_org_name",
        header: "Vendor",
        cell: (r: Report) => (
          <Button variant="inline-link" onClick={() => onSelectReport(r.id)}>
            {r.service_org_name || "(extracting…)"}
          </Button>
        ),
        width: 200,
        minWidth: 160,
      },
      {
        id: "scope_title",
        header: "Scope",
        cell: (r: Report) => (
          <span
            title={r.scope_title ?? ""}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block",
            }}
          >
            {r.scope_title ?? "—"}
          </span>
        ),
        minWidth: 240,
        maxWidth: 380,
      },
      {
        id: "period_end",
        header: "Period end",
        cell: (r: Report) => r.period_end || "—",
        width: 130,
        minWidth: 110,
      },
      {
        id: "status",
        header: "Status",
        cell: (r: Report) => {
          const s = lifecycleOf(r);
          return <StatusIndicator type={lifecycleColor(s)}>{s}</StatusIndicator>;
        },
        width: 160,
        minWidth: 140,
      },
      {
        id: "updated",
        header: "Updated",
        cell: (r: Report) => formatET(r.updated_at),
        width: 160,
        minWidth: 150,
      },
      {
        id: "comments",
        header: "Notes",
        cell: (r: Report) => (
          <CommentsCell
            kind="report"
            targetId={r.id}
            targetLabel={r.service_org_name}
            count={r.comment_count ?? 0}
            onChange={refresh}
          />
        ),
        width: 110,
        minWidth: 90,
      },
      {
        id: "actions",
        header: "",
        cell: (r: Report) => (
          <Button
            iconName="remove"
            ariaLabel={`Delete ${r.service_org_name}`}
            onClick={() => setDeleteTarget(r)}
          />
        ),
        width: 70,
        minWidth: 60,
      },
    ],
    [onSelectReport, refresh]
  );

  const groups = useMemo(() => groupByFY(reports), [reports]);

  return (
    <Box padding={{ top: "l" }}>
      <SpaceBetween size="l">
        <Header
          variant="h1"
          // #121: the top counter is active (non-complete) reports — completed ones
          // have their own group + counter, so this total shouldn't inflate.
          counter={`(${reports.filter((r) => r.lifecycle_status !== "complete").length})`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={refresh}>Refresh</Button>
              <Button
                variant="primary"
                onClick={() => (profileComplete === false ? setGateOpen(true) : setUploadOpen(true))}
              >
                Upload SOC 1
              </Button>
            </SpaceBetween>
          }
        >
          Reports
        </Header>
        {profileComplete === false && (
          <Alert
            type="info"
            header="Set your fiscal year end first"
            action={<Button onClick={() => onNavigateProfile()}>Open Company profile</Button>}
          >
            The AI needs your fiscal year end before it can run the C2 period-coverage check. Set it in
            Company profile, then upload.
          </Alert>
        )}
        {reports.length === 0 && !loading && (
          <Box textAlign="center" color="inherit" padding="xl">
            <Box variant="strong">No reports yet</Box>
            <Box variant="p" color="inherit">
              Upload a SOC 1 PDF to start.
            </Box>
          </Box>
        )}
        {groups.map((g) => (
          <Table
            key={g.label}
            loading={loading && reports.length === 0}
            loadingText="Loading reports"
            header={
              <Header variant="h2" counter={`(${g.reports.length})`}>
                {g.label}
              </Header>
            }
            items={g.reports}
            columnDefinitions={columns}
          />
        ))}
      </SpaceBetween>

      <UploadModal
        visible={uploadOpen}
        onDismiss={() => setUploadOpen(false)}
        onUploaded={(id) => {
          setUploadOpen(false);
          void refresh();
          onSelectReport(id);
        }}
      />

      <Modal
        visible={gateOpen}
        onDismiss={() => setGateOpen(false)}
        header="Set your fiscal year end first"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setGateOpen(false)}>Not now</Button>
              <Button
                variant="primary"
                onClick={() => {
                  setGateOpen(false);
                  onNavigateProfile();
                }}
              >
                Open Company profile
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        A SOC 1 can't be submitted until your fiscal year end is set — the AI needs it to run the C2
        period-coverage check. Set it in Company profile, then upload.
      </Modal>

      <Modal
        visible={!!deleteTarget}
        onDismiss={() => {
          setDeleteTarget(null);
          setDeleteConfirmText("");
        }}
        header="Delete report"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!confirmMatch || deleting}
                loading={deleting}
                onClick={confirmDelete}
              >
                Delete report
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <Box>
            This permanently deletes the report and every associated scope check, exception, CUEC, and action
            item. The PDF stays in S3 until the next nightly cleanup.
          </Box>
          <Box>
            Type the vendor name <strong>{expectedConfirm}</strong> to confirm:
          </Box>
          <Textarea
            value={deleteConfirmText}
            onChange={({ detail }) => setDeleteConfirmText(detail.value)}
            rows={1}
          />
        </SpaceBetween>
      </Modal>
    </Box>
  );
}
